// tslint:disable:no-commented-code
// tslint:disable:no-duplicate-string
// tslint:disable:only-arrow-functions
// tslint:disable:no-big-function

import { SimpleSelectOptionType } from '@epicuro-next/features/landing-panel';
import {
  IDynamicBindableValue,
  IDynamicInput,
  IDynamicItem,
  IDynamicSimpleText,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { mergeMultipleConfigs } from '@epicuro-next/platform/dynamic-components-utils';

import {
  createInstance,
  createInstanceWithChildren,
  getIdProvider,
} from '../../../utils/dynamic-items-utils';
import {
  createApiRequestPayloadPropertiesProvider,
  getApiResponseTransformMethod,
} from '../common/best-performing-articles/best-performing-articles-helpers';
import {
  createSquarePanelFromRecommendations,
  ids as templateIds,
  propertyNames as templatePropertyNames,
} from '../common/square-panel-from-recommendations';

import { hideGenderIcon } from './helpers/hide-gender-icon';

const propertyNames = {
  ...templatePropertyNames,
  filterMissingImages: 'filterMissingImages',
  selectedDayRange: 'selectedDayRange',
  selectedCategory: 'selectedCategory',
  orderType: 'orderType',
  brandOrganisationId: 'brandOrganisationId',
  allCategoriesText: 'allCategoriesText',
};

export const squarePanelFromRecommendationsBestPerformingArticles =
  mergeMultipleConfigs(
    createSquarePanelFromRecommendationsBestPerformingArticles().styleWrapper,
    [hideGenderIcon],
  );

export function createSquarePanelFromRecommendationsBestPerformingArticles() {
  const bestPerformingArticlesDayRangeSelectId =
    'best-performing-articles-day-range-select';
  const categorySelectId = 'category-select';

  const days = [
    { name: '7 days', value: '7' },
    { name: '14 days', value: '14' },
    { name: '30 days', value: '30' },
  ];
  const defaultSelectedDayIndex = 0;

  const dayRangeSelectOptions: SimpleSelectOptionType[] = [];
  for (const day of days) {
    dayRangeSelectOptions.push({
      value: day.value,
      displayValue: day.name,
    });
  }

  const defaultSelectedDayOption: SimpleSelectOptionType =
    dayRangeSelectOptions[defaultSelectedDayIndex];

  const defaultSelectedCategoryOption = {
    displayValue: 'Show all',
    value: undefined,
  };

  const periodInDaysProvider: IDynamicBindableValue = {
    ...getIdProvider(
      bestPerformingArticlesDayRangeSelectId,
      'selectionChanged',
    ),
    startWith: {
      value: defaultSelectedDayOption,
    },
    transform: function (option: SimpleSelectOptionType): string {
      return option.value;
    }.toString(),
  };

  const categoryProvider: IDynamicBindableValue = {
    ...getIdProvider(categorySelectId, 'selectionChanged'),
    startWith: {
      value: defaultSelectedCategoryOption,
    },
    transform: function (option: SimpleSelectOptionType) {
      return option;
    }.toString(),
  };

  const apiRequestPayloadPropertiesProvider =
    createApiRequestPayloadPropertiesProvider({
      datePeriodDays: periodInDaysProvider,
      category: categoryProvider,
      brandOrganisationId: getIdProvider(
        templateIds.squarePanelRoot,
        propertyNames.brandOrganisationId,
      ),
      orderType: getIdProvider(
        templateIds.squarePanelRoot,
        propertyNames.orderType,
      ),
      filterMissingImages: getIdProvider(
        templateIds.squarePanelRoot,
        propertyNames.filterMissingImages,
      ),
    });

  const template = createSquarePanelFromRecommendations(
    'LandingPage',
    {
      apiRequestPayloadPropertiesProvider,
    },
    getApiResponseTransformMethod(),
  );

  addAdditionalArguments(template.root);

  const simpleDayRangeSelect = createInstance({
    id: bestPerformingArticlesDayRangeSelectId,
    contentTypeId: 'dynamic-elements-simple-select',
    inputs: {
      options: dayRangeSelectOptions,
      selectedValue: getIdProvider(
        template.root.id,
        propertyNames.selectedDayRange,
      ),
    },
  });

  // https://epicuropumatest.epicurotesting.com/api/v2/reporting/topproducts/filters
  const categorySelect = createInstance({
    id: 'category-select',
    contentTypeId: 'dynamic-elements-simple-select',
    inputs: {
      options: {
        provider: 'ProductFiltersProvider',
        property: { currentSearchQuery: {} },
        transform: function (res: any): {
          displayValue: string;
          value: string | undefined;
        }[] {
          const categoryFilter = res.filters.find(
            (filter: any) => filter.displayName === 'Category',
          );

          const mapped =
            categoryFilter?.values?.map(
              (category: { displayName: string; value: string }) => ({
                displayValue: category.displayName,
                value: category.value,
              }),
            ) || [];

          const allValues = mapped.map((item: any) => item.value).join(',');
          mapped.unshift({
            displayValue: 'Show all',
            value: mapped.length ? allValues : undefined,
          });

          return mapped;
        }.toString(),
      },
      selectedValue: getIdProvider(
        template.root.id,
        propertyNames.selectedCategory,
      ),
    },
  });

  // This ping pong to root and simpleDayRangeSelect is needed as id providing does not work on an element of itself.
  (template.root.data as { [key: string]: IDynamicInput })[
    propertyNames.selectedDayRange
  ] = periodInDaysProvider;
  (template.root.data as { [key: string]: IDynamicInput })[
    propertyNames.selectedCategory
  ] = categoryProvider;

  const firstPartOfHeaderText: IDynamicSimpleText = {
    contentTypeId: 'dynamic-elements-simple-text',
    styles: {
      style: {
        'white-space': 'pre',
      },
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
      },
    },
    inputs: {
      text: {
        ...getIdProvider(template.root.id, propertyNames.headerText),
      },
    },
  };

  const brandOrgText: IDynamicSimpleText = {
    id: 'brand-org-text',
    contentTypeId: 'dynamic-elements-simple-text',
    styles: {
      style: {
        'white-space': 'pre',
      },
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
      },
    },
    inputs: {
      text: {
        provider: 'GlobalStoreProviderService',
        property: {
          path: 'accounts.activeAccount.domainId',
        },
        transform: function (domainId: string) {
          return `${domainId} `;
        }.toString(),
      },
    },
  };

  const spaceBetweenPrefixAndDaysSelection: IDynamicSimpleText = {
    contentTypeId: 'dynamic-elements-simple-text',
    styles: {
      style: {
        'white-space': 'pre',
      },
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
      },
    },
    inputs: {
      text: ' ',
    },
  };

  const nrOfDaysText: IDynamicSimpleText = {
    id: 'best-performing-articles-nr-of-days-text',
    contentTypeId: 'dynamic-elements-simple-text',
    styles: {
      style: {
        'text-decoration': 'underline',
      },
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
      },
      classes: ['cursor-pointer'],
    },
    inputs: {
      text: periodInDaysProvider,
    },
  };

  const dropdownForDaysPeriod: IDynamicItem = {
    contentTypeId: 'dynamic-elements-dropdown-wrapper',
    inputs: {
      triggerContent: nrOfDaysText,
      dropdownContent: simpleDayRangeSelect,
    },
  };

  const categoryText: IDynamicSimpleText = {
    id: 'category-text',
    contentTypeId: 'dynamic-elements-simple-text',
    styles: {
      style: {
        'text-decoration': 'underline',
        'max-width': '150px',
      },
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
      },
      classes: ['cursor-pointer', 'text-truncate', 'd-block'],
    },
    data: {
      selectedCategory: categoryProvider,
    },
    inputs: {
      text: {
        values: [
          {
            provider: 'self',
            property: 'selectedCategory',
          },
          getIdProvider(
            templateIds.squarePanelRoot,
            propertyNames.allCategoriesText,
          ),
        ],
        transform: function ([selectedCategory, allCategoriesText]: [
          SimpleSelectOptionType,
          string,
        ]) {
          return selectedCategory.displayValue === 'Show all'
            ? allCategoriesText
            : selectedCategory.value ?? allCategoriesText;
        }.toString(),
      },
    },
  };

  const dropdownForCategory: IDynamicItem = {
    contentTypeId: 'dynamic-elements-dropdown-wrapper',
    inputs: {
      triggerContent: categoryText,
      dropdownContent: categorySelect,
    },
  };

  const daysText: IDynamicSimpleText = {
    contentTypeId: 'dynamic-elements-simple-text',
    styles: {
      style: {
        'white-space': 'pre',
      },
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
      },
    },
    inputs: {
      text: ' days',
    },
  };

  const inText: IDynamicSimpleText = {
    contentTypeId: 'dynamic-elements-simple-text',
    styles: {
      style: {
        'white-space': 'pre',
      },
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
      },
    },
    inputs: {
      text: 'in',
    },
  };

  const itemsInHeaderWrapper = createInstanceWithChildren({
    id: 'best-performing-articles-header-wrapper',
    contentTypeId: 'dynamic-collections-renderer',
    styles: {
      classes: ['d-flex', 'flex-row', 'justify-content-center', 'h4'],
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
      },
      style: {
        'margin-bottom': '14px',
      },
    },
    children: [
      firstPartOfHeaderText,
      spaceBetweenPrefixAndDaysSelection,
      brandOrgText,
      dropdownForCategory,
      spaceBetweenPrefixAndDaysSelection,
      inText,
      spaceBetweenPrefixAndDaysSelection,
      dropdownForDaysPeriod,
      daysText,
    ],
  });

  template.headerContainer.children = [itemsInHeaderWrapper];

  return { ...template };
}

function addAdditionalArguments(element: { data: { [key: string]: unknown } }) {
  element.data[propertyNames.orderType] = 0;
  element.data[propertyNames.headerText] = 'US Re-Ordered in the last';
  element.data[propertyNames.filterMissingImages] = false;
  element.data[propertyNames.allCategoriesText] = 'All Categories';
}
